import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Spin, Select, DatePicker } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import queryKeys from "common/queryKey";
import { useScrollFetchPost2 } from "hooks/useScrollFetchPost2";
import { getUserInfo, getUserList } from "redux/selectors";
import { useSelector } from "react-redux";
import { generateNiceWeekFromWeek } from "./useWeekData";
import { APPROVED, DECLINED, DRAFT, ONGOING, PENDING, WITHDRAWN } from "./constants";

// "Custom" range-type dropdown. "custom" shows two editable month pickers
// (From / To). "this_month" shows a single editable month picker, defaulted
// to the current month. "all" shows no pickers at all.
// (Quarter / Year presets removed per design — month-level filtering is enough.)
const RANGE_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "custom", label: "Custom" },
  { value: "this_month", label: "By Month" },
];

// All statuses we ever fetch from the backend — filtering itself now happens
// entirely on the frontend, so we always pull the full unfiltered dataset once.
const ALL_STATUSES = [PENDING, APPROVED, DECLINED, ONGOING, WITHDRAWN, DRAFT];

// --- Filter persistence -----------------------------------------------
// The filter controls (status / range type / dates) live in local
// useState. If this component unmounts when the user navigates to the
// timesheet detail view and remounts on return, that state is lost and
// the filters silently reset. We persist the filter values to
// sessionStorage (survives navigation + refresh within the tab, clears
// when the tab closes) and rehydrate on mount.
const FILTER_STORAGE_KEY = "timesheetHistoryFilters";

const loadStoredFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const TimeSheetHistory = ({ handleClickTimeSheet, setShowTimesheet }) => {
  const { getAdminApprovalTimesheet } = queryKeys;

  const { key, url } = getAdminApprovalTimesheet("all");

  const userInfo = useSelector(getUserInfo);

  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  // --- Filters (frontend-only — the fetch below always pulls the full set) ---
  const STATUS_FILTER_OPTIONS = useMemo(
    () => [
      { value: "All", label: "All" },
      { value: "pending_submission", label: "Pending Submission" },
      { value: "pending_approval", label: "Pending Approval" },
      { value: "approved", label: "Approved" },
      { value: "declined", label: "Declined" },
    ],
    []
  );

  // Read persisted filters once on mount.
  const storedFilters = useMemo(() => loadStoredFilters(), []);

  const [statusFilter, setStatusFilter] = useState(
    storedFilters?.statusFilter ?? "All"
  );
  const [rangeType, setRangeType] = useState(
    storedFilters?.rangeType ?? "this_month"
  );

  // Used only when rangeType === "this_month" — a single editable month value.
  const [selectedMonth, setSelectedMonth] = useState(
    storedFilters?.selectedMonth ? dayjs(storedFilters.selectedMonth) : dayjs()
  );

  // Used only when rangeType === "custom" — two independent month values.
  const [fromDate, setFromDate] = useState(
    storedFilters?.fromDate ? dayjs(storedFilters.fromDate) : null
  );
  const [toDate, setToDate] = useState(
    storedFilters?.toDate ? dayjs(storedFilters.toDate) : null
  );

  // Persist filters to sessionStorage whenever any of them change, so
  // they survive an unmount/remount (navigating away and back) or a
  // page refresh within the same tab.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          statusFilter,
          rangeType,
          selectedMonth: selectedMonth ? selectedMonth.toISOString() : null,
          fromDate: fromDate ? fromDate.toISOString() : null,
          toDate: toDate ? toDate.toISOString() : null,
        })
      );
    } catch {
      // sessionStorage unavailable (private browsing, quota, etc.) —
      // fail silently, filters just won't persist this session.
    }
  }, [statusFilter, rangeType, selectedMonth, fromDate, toDate]);

  // Effective bounds actually applied to the data, derived from whichever
  // mode is active. "all" resolves to null/null, which skips the frontend
  // date filter below entirely.
  const effectiveFrom = useMemo(() => {
    if (rangeType === "this_month") return selectedMonth.startOf("month");
    if (rangeType === "custom") return fromDate;
    return null;
  }, [rangeType, selectedMonth, fromDate]);

  const effectiveTo = useMemo(() => {
    if (rangeType === "this_month") return selectedMonth.endOf("month");
    if (rangeType === "custom") return toDate;
    return null;
  }, [rangeType, selectedMonth, toDate]);

  const handleRangeTypeChange = (value) => {
    setRangeType(value);
    if (value === "this_month") {
      // Reset to the current month whenever switching into "By Month"
      setSelectedMonth(dayjs());
    }
    if (value !== "custom") {
      // Leaving "Custom" clears any manual selection so switching back
      // starts from a blank pair of pickers.
      setFromDate(null);
      setToDate(null);
    }
  };

  // Custom-range handlers: keep From <= To at all times. If the person picks
  // a From date that's after the current To date, clear To rather than allow
  // an invalid range (the disabledDate props below also prevent this via UI).
  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (value && toDate && value.isAfter(toDate, "month")) {
      setToDate(null);
    }
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    if (value && fromDate && value.isBefore(fromDate, "month")) {
      setFromDate(null);
    }
  };

  const disableFromDate = (date) => !!(toDate && date.isAfter(toDate, "month"));
  const disableToDate = (date) => !!(fromDate && date.isBefore(fromDate, "month"));

  // Fetch key no longer depends on the filters — everything below is filtered
  // client-side, so we just fetch (and cache) the full dataset once.
  const scrollKey = useMemo(() => (Array.isArray(key) ? key : [key]), [key]);

  const { data, isLoading: isHistoryLoading } = useScrollFetchPost2(scrollKey, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
    extra: {
      weeks: [],
      statusArray: ALL_STATUSES,
      employeeId: [userInfo.userid],
      country: country,
    },
  });

  const timesheetsData = useMemo(() => {
    if (!data) return [];
    return data?.pages?.reduce((acc, page) => {
      if (page?.response?.response === undefined) return acc;
      return [...acc, ...page?.response?.response];
    }, []);
  }, [data]);

  const [timeSheets, setTimeSheets] = React.useState([]);

  useEffect(() => {
    if (timesheetsData) {
      const data = timesheetsData
        .sort(
          (a, b) =>
            new Date(b.week.split("-")[1]) - new Date(a.week.split("-")[1])
        )
        .map((timesheet) => {
          const timeSheet = timesheet?.timeSheet || [];

          const totalHours = timeSheet
            .reduce((acc, section) => {
              return (
                acc +
                (parseFloat(section.mon) || 0) +
                (parseFloat(section.tue) || 0) +
                (parseFloat(section.wed) || 0) +
                (parseFloat(section.thu) || 0) +
                (parseFloat(section.fri) || 0) +
                (parseFloat(section.sat) || 0) +
                (parseFloat(section.sun) || 0)
              );
            }, 0)
            .toFixed(3);

          // "Chargeable" = billed to an actual client/matter (client is
          // neither "nc" Non-Chargeable nor "ap" Approved Absence).
          const chargeable = timeSheet.filter(
            (section) =>
              section.client && section.client !== "nc" && section.client !== "ap"
          );

          const chargeableHours = chargeable
            .reduce((acc, section) => {
              return (
                acc +
                (parseFloat(section.mon) || 0) +
                (parseFloat(section.tue) || 0) +
                (parseFloat(section.wed) || 0) +
                (parseFloat(section.thu) || 0) +
                (parseFloat(section.fri) || 0) +
                (parseFloat(section.sat) || 0) +
                (parseFloat(section.sun) || 0)
              );
            }, 0)
            .toFixed(3);

          // "NC / Absence" = Non-Chargeable ("nc") + Approved Absence ("ap").
          const ncAbsence = timeSheet.filter(
            (section) => section.client === "nc" || section.client === "ap"
          );

          const ncAbsenceHours = ncAbsence
            .reduce((acc, section) => {
              return (
                acc +
                (parseFloat(section.mon) || 0) +
                (parseFloat(section.tue) || 0) +
                (parseFloat(section.wed) || 0) +
                (parseFloat(section.thu) || 0) +
                (parseFloat(section.fri) || 0) +
                (parseFloat(section.sat) || 0) +
                (parseFloat(section.sun) || 0)
              );
            }, 0)
            .toFixed(3);

          const approved = timeSheet.filter(
            (section) =>
              section.status === "approved" ||
              section.status === "ongoing" ||
              section.client === "ap" ||
              (section.client === "nc" && section.status === "approved") || // Include all approved NC entries
              (section.client === "c" && section.status === "approved") // Include all approved C entries
          );

          const approvedHours = approved
            .reduce((acc, section) => {
              return (
                acc +
                (parseFloat(section.mon) || 0) +
                (parseFloat(section.tue) || 0) +
                (parseFloat(section.wed) || 0) +
                (parseFloat(section.thu) || 0) +
                (parseFloat(section.fri) || 0) +
                (parseFloat(section.sat) || 0) +
                (parseFloat(section.sun) || 0)
              );
            }, 0)
            .toFixed(3);

          const rejected = timeSheet.filter(
            (section) => section.status === "declined"
          );

          const rejectedHours = rejected
            .reduce((acc, section) => {
              return (
                acc +
                (parseFloat(section.mon) || 0) +
                (parseFloat(section.tue) || 0) +
                (parseFloat(section.wed) || 0) +
                (parseFloat(section.thu) || 0) +
                (parseFloat(section.fri) || 0) +
                (parseFloat(section.sat) || 0) +
                (parseFloat(section.sun) || 0)
              );
            }, 0)
            .toFixed(3);

          const pending_submission = timeSheet.filter(
            (section) =>
              section?.status === "draft" || section?.status === "withdrawn"
          );

          const draftHours = pending_submission
            .reduce((acc, section) => {
              return (
                acc +
                (parseFloat(section.mon) || 0) +
                (parseFloat(section.tue) || 0) +
                (parseFloat(section.wed) || 0) +
                (parseFloat(section.thu) || 0) +
                (parseFloat(section.fri) || 0) +
                (parseFloat(section.sat) || 0) +
                (parseFloat(section.sun) || 0)
              );
            }, 0)
            .toFixed(3);

          // Raw week start/end, kept as dayjs objects so the frontend date
          // filter can compare against the selected From/To months.
          const weekParts = timesheet?.week ? timesheet.week.split("-") : [];
          const weekStart = weekParts[0] ? dayjs(new Date(weekParts[0])) : null;
          const weekEnd = weekParts[1] ? dayjs(new Date(weekParts[1])) : null;

          return {
            period: timesheet?.week
              ? generateNiceWeekFromWeek(timesheet?.week)
              : "-",
            weekStart,
            weekEnd,
            approved: Number(approvedHours),
            rejected: Number(rejectedHours),
            pending_approval: Math.max(
              0,
              Number(totalHours) -
              Number(approvedHours) -
              Number(rejectedHours) -
              Number(draftHours)
            ),
            pending_submission: Number(draftHours),
            total_hours: Number(totalHours),
            chargeable_hours: Number(chargeableHours),
            nc_absence_hours: Number(ncAbsenceHours),
            timesheetSubmittedDate: timesheet?.timesheetSubmittedDate || "-",
          };
        });
      setTimeSheets(data);
    }
  }, [timesheetsData]);

  // --- Frontend filtering: status + month range are both applied here,
  // entirely client-side, against the full fetched dataset. ---
  const filteredTimeSheets = useMemo(() => {
    return timeSheets.filter((sheet) => {
      let statusMatch = true;
      switch (statusFilter) {
        case "pending_submission":
          statusMatch =
            sheet.timesheetSubmittedDate === "-" || sheet.pending_submission > 0;
          break;
        case "pending_approval":
          statusMatch = sheet.pending_approval > 0;
          break;
        case "approved":
          statusMatch = sheet.total_hours > 0 &&
            Math.abs(sheet.approved - sheet.total_hours) < 0.001;
          break;
        case "declined":
          statusMatch = sheet.rejected > 0;
          break;
        default:
          statusMatch = true;
      }

      if (!statusMatch) return false;

      if (sheet.weekStart) {
        // Use the week's end (fallback to start if missing) for the lower
        // bound check, and the week's start for the upper bound check, so a
        // week that only partially overlaps the selected month range (e.g.
        // 29 Jun - 05 Jul overlapping a "July" filter) still matches, instead
        // of being excluded just because it started in June.
        const weekRangeEnd = sheet.weekEnd || sheet.weekStart;

        if (effectiveFrom && weekRangeEnd.isBefore(effectiveFrom.startOf("month"))) {
          return false;
        }
        if (effectiveTo && sheet.weekStart.isAfter(effectiveTo.endOf("month"))) {
          return false;
        }
      }

      return true;
    });
  }, [timeSheets, statusFilter, effectiveFrom, effectiveTo]);

  const summary = useMemo(() => {
    return filteredTimeSheets.reduce(
      (acc, sheet) => ({
        totalHours: acc.totalHours + (sheet.total_hours || 0),
        approved: acc.approved + (sheet.approved || 0),
        pending: acc.pending + (sheet.pending_approval || 0),
        pendingSubmission: acc.pendingSubmission + (sheet.pending_submission || 0),
      }),
      { totalHours: 0, approved: 0, pending: 0, pendingSubmission: 0 }
    );
  }, [filteredTimeSheets]);

  const approvedShare =
    summary.totalHours > 0
      ? Math.min(100, Math.round((summary.approved / summary.totalHours) * 100))
      : 0;

  const formatHours = (value) => {
    const rounded = Math.round((value || 0) * 10) / 10;
    return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1)}`;
  };

  // Formats the submitted date/time to 12-hour format, e.g. "19 Aug 2026, 03:45 PM".
  const formatSubmittedDateTime = (value) => {
    if (!value || value === "-") return value;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("hh:mm A") : value;
  };

  return (
    <HistoryWrap>
      <Spin spinning={isHistoryLoading}>
        <FilterBar>
          <WeekLoggedText>Total Week Logged: {filteredTimeSheets.length}</WeekLoggedText>

          <FilterControls>
            <StyledSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS.map(({ value, label }) => ({ value, label }))}
              popupMatchSelectWidth={false}
            />

            <StyledSelect
              value={rangeType}
              onChange={handleRangeTypeChange}
              options={RANGE_TYPE_OPTIONS}
              suffixIcon={<FilterOutlined />}
              popupMatchSelectWidth={false}
            />

            {rangeType === "custom" && (
              <>
                <StyledDatePicker
                  picker="month"
                  placeholder="From"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  format="MMM, YYYY"
                  allowClear
                  disabledDate={disableFromDate}
                />

                <StyledDatePicker
                  picker="month"
                  placeholder="To"
                  value={toDate}
                  onChange={handleToDateChange}
                  format="MMM, YYYY"
                  allowClear
                  disabledDate={disableToDate}
                />
              </>
            )}

            {rangeType === "this_month" && (
              <StyledDatePicker
                picker="month"
                placeholder="Month"
                value={selectedMonth}
                onChange={(value) => setSelectedMonth(value || dayjs())}
                format="MMM, YYYY"
                allowClear={false}
              />
            )}

            {/* rangeType === "all" -> no date pickers rendered at all */}
          </FilterControls>
        </FilterBar>

        <SummaryPanel>
          <SummaryCard>
            <SummaryLabelRow>Total Hours</SummaryLabelRow>
            <SummaryValue>{formatHours(summary.totalHours)}</SummaryValue>
            <SummaryCaption>Hours Logged</SummaryCaption>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabelRow>Approved</SummaryLabelRow>
            <SummaryValue>{formatHours(summary.approved)}</SummaryValue>
            <ProgressTrack>
              <ProgressFill $percent={approvedShare} />
            </ProgressTrack>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabelRow>Pending Approval</SummaryLabelRow>
            <SummaryValue>{formatHours(summary.pending)}</SummaryValue>
            <SummaryCaption>Hours Awaiting Manager review</SummaryCaption>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabelRow>Pending Submission</SummaryLabelRow>
            <SummaryValue>{formatHours(summary.pendingSubmission)}</SummaryValue>
            <SummaryCaption>Hours Awaiting Your Submission</SummaryCaption>
          </SummaryCard>
        </SummaryPanel>

        <LegendRow>
          <LegendItem>
            <LegendDot $tone="declined" /> Declined
          </LegendItem>
          <LegendItem>
            <LegendDot $tone="pending" /> Pending Approval
          </LegendItem>
          <LegendItem>
            <LegendDot $tone="approved" /> Approved
          </LegendItem>
        </LegendRow>

        <CardGrid>
          {filteredTimeSheets.map((sheet, index) => {
            const isSubmitted = sheet.timesheetSubmittedDate !== "-";
            return (
              <PeriodCard
                key={`${sheet.period}-${index}`}
                onClick={() => handleClickTimeSheet(sheet?.period)}
              >
                <PeriodTitle>{sheet.period}</PeriodTitle>

                <StatRow>
                  <Stat>
                    <StatLabel>Total Hours</StatLabel>
                    <StatValue>{formatHours(sheet.total_hours)}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>Chargeable</StatLabel>
                    <StatValue>{formatHours(sheet.chargeable_hours)}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel>NC / Absence</StatLabel>
                    <StatValue>{formatHours(sheet.nc_absence_hours)}</StatValue>
                  </Stat>
                </StatRow>

                <CardDivider />

                <CardFooter>
                  {isSubmitted && sheet.pending_submission === 0 ? (
                    <SubmittedText>
                      Submitted: {formatSubmittedDateTime(sheet.timesheetSubmittedDate)}
                    </SubmittedText>
                  ) : (
                    <PendingSubmissionBadge title="Pending Submission">Pending Submission</PendingSubmissionBadge>
                  )}

                  {isSubmitted && (
                    <BadgeRow>
                      {sheet.rejected > 0 && (
                        <CountBadge $tone="declined">
                          {Math.round(sheet.rejected)}
                        </CountBadge>
                      )}
                      {sheet.pending_approval > 0 && (
                        <CountBadge $tone="pending">
                          {Math.round(sheet.pending_approval)}
                        </CountBadge>
                      )}
                      {sheet.approved > 0 && (
                        <CountBadge $tone="approved">
                          {Math.round(sheet.approved)}
                        </CountBadge>
                      )}
                    </BadgeRow>
                  )}
                </CardFooter>
              </PeriodCard>
            );
          })}

          {!isHistoryLoading && filteredTimeSheets.length === 0 && (
            <EmptyState>No timesheet history yet.</EmptyState>
          )}
        </CardGrid>
      </Spin>
    </HistoryWrap>
  );
};

export default TimeSheetHistory;

// Lighter, halo-style tones matching the Figma spec: a soft pastel
// background ("bg") with a darker accent ("dot") used for the small inner
// dot in the legend and for the count-badge text.
const TONES = {
  declined: { bg: "#FFD9DD", dot: "#D93025" },
  pending: { bg: "#FFDDA3", dot: "#774B00" },
  approved: { bg: "#D1FAE5", dot: "#1B5E49" },
};



const HistoryWrap = styled.div`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const WeekLoggedText = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const StyledSelect = styled(Select)`
  min-width: 150px;

  .ant-select-selector {
    border-radius: 999px !important;
    border-color: #e2e4e9 !important;
    padding: 0 14px !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  border-radius: 999px;
  border-color: #e2e4e9;
  padding: 5px 14px;
`;

// Grey background panel housing the four summary stats, each rendered as
// its own white rounded card sitting on top of the grey panel.
const SummaryPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  background: #f5f6f8;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 18px 20px;
`;

const SummaryLabelRow = styled.div`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #636980;
  margin-bottom: 10px;
`;

const SummaryValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #16181d;
  margin-bottom: 8px;
`;

const SummaryCaption = styled.div`
  font-size: 12px;
  color: #636980;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #eef0f3;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #1faa59;
  width: ${({ $percent }) => $percent}%;
  transition: width 0.3s ease;
`;

const LegendRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 18px;
  margin-bottom: 35px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #414944;
`;

const LegendDot = styled.span`
  position: relative;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $tone }) => TONES[$tone].bg};
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $tone }) => TONES[$tone].dot};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

// Border color updated to the soft green shown in the design (was #edeef2),
// with a stronger green on hover.
const PeriodCard = styled.div`
  background: #ffffff;
  border: 1px solid #bfebd2;
  border-radius: 14px;
  padding: 18px 20px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: #1faa59;
    box-shadow: 0 4px 14px rgba(16, 24, 40, 0.06);
  }
`;

const PeriodTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1F243B;
  margin-bottom: 14px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const CardDivider = styled.div`
  border-top: 1px solid #edeef2;
  margin-bottom: 14px;
`;

const Stat = styled.div`
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #9297a1;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #16181d;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 8px;
  min-height: 24px;
`;

const SubmittedText = styled.div`
  font-size: 14px;
  color: #414944;
  flex: 1 1 auto;
  min-width: 0;
`;

const PendingSubmissionBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${TONES.pending.bg};
  color: ${TONES.pending.dot};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  min-width: 0;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  min-width: 0;
`;

const CountBadge = styled.span`
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $tone }) => TONES[$tone].dot};
  background: ${({ $tone }) => TONES[$tone].bg};
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
  color: #9297a1;
  font-size: 13px;
`;